import axios, { AxiosError, AxiosRequestHeaders } from 'axios';
import toast from 'react-hot-toast';
import { IS_PROD_ENV } from './constants';
import { ApiError, ApiErrorPayload, CustomAxiosRequestConfig } from './types';

const SENSITIVE_HEADER_KEYS = new Set(['authorization']);
const SENSITIVE_BODY_KEYS = new Set([
  'token',
  'accesstoken',
  'refreshtoken',
  'password',
  'authorization',
]);

const MAX_REDACT_DEPTH = 8;

const redactHeaders = (headers: AxiosRequestHeaders | undefined) => {
  if (!headers) {
    return undefined;
  }

  const sanitized: Record<string, unknown> = {};
  Object.entries(headers).forEach(([key, value]) => {
    sanitized[key] = SENSITIVE_HEADER_KEYS.has(key.toLowerCase()) ? '[REDACTED]' : value;
  });
  return sanitized;
};

const redactBody = (data: unknown, depth = 0): unknown => {
  if (depth === 0 && typeof data === 'string') {
    try {
      return redactBody(JSON.parse(data), depth);
    } catch {
      return data;
    }
  }

  if (!data || typeof data !== 'object') {
    return data;
  }

  if (depth >= MAX_REDACT_DEPTH) {
    return '[TRUNCATED]';
  }

  if (Array.isArray(data)) {
    return data.map(item => redactBody(item, depth + 1));
  }

  const sanitized: Record<string, unknown> = {};
  Object.entries(data as Record<string, unknown>).forEach(([key, value]) => {
    sanitized[key] = SENSITIVE_BODY_KEYS.has(key.toLowerCase())
      ? '[REDACTED]'
      : redactBody(value, depth + 1);
  });
  return sanitized;
};

const axiosErrorEnvelope = (error: AxiosError) => ({
  message: error.message,
  code: error.code,
  status: error.response?.status,
  method: error.config?.method,
  url: error.config?.url,
});

const causeOf = (error: unknown): unknown => {
  return error instanceof Error && 'cause' in error && error.cause ? error.cause : error;
};

/**
 * Production keeps a minimal envelope only — no APM collects these, so logging nothing would
 * leave no client diagnostics at all. Never unwraps an AxiosError: axios sets `cause` itself,
 * and unwrapping would drop the status/url this exists to record.
 */
export const logSafeAxiosError = (rawError: unknown) => {
  const error = axios.isAxiosError(rawError) ? rawError : causeOf(rawError);

  if (!axios.isAxiosError(error)) {
    if (!IS_PROD_ENV) {
      console.error(error);
      return;
    }

    console.error(error instanceof Error ? `${error.name}: ${error.message}` : '[non-Error thrown]');
    return;
  }

  if (IS_PROD_ENV) {
    console.error(axiosErrorEnvelope(error));
    return;
  }

  console.error({
    ...axiosErrorEnvelope(error),
    headers: redactHeaders(error.config?.headers),
    data: redactBody(error.config?.data),
    responseData: redactBody(error.response?.data),
  });
};

export const showErrorToastOnce = (message: string) => {
  toast.error(message, { id: message });
};

/** Objects only: a string body can't hold the reported-to-user mark, so it would double-toast. */
export const getErrorPayload = (error: AxiosError): ApiError | AxiosError => {
  const data = error.response?.data;
  return data && typeof data === 'object' ? (data as ApiError) : error;
};

/**
 * The client rejects with the response body, not with the AxiosError, so there is no
 * `response.status` to read here: the API's own code arrives as `error` in the payload.
 */
export const apiErrorCode = (error: unknown): string | undefined => {
  const code = (error as ApiErrorPayload | undefined)?.error;
  return typeof code === 'string' ? code : undefined;
};

export const shouldSuppressToast = (error: AxiosError): boolean => {
  return !!(error.config as CustomAxiosRequestConfig | undefined)?.skipGenericToast;
};

const reportedToUser = new WeakSet<object>();

export const markReportedToUser = <T>(payload: T): T => {
  if (payload && typeof payload === 'object') {
    reportedToUser.add(payload as object);
  }

  return payload;
};

const isMarkedAsReported = (value: unknown): boolean => {
  return !!value && typeof value === 'object' && reportedToUser.has(value as object);
};

const wasReportedToUser = (error: unknown): boolean => {
  // Mark is checked on the cause too; the status heuristics below aren't, since they only
  // hold for errors that went through apiClient.
  if (isMarkedAsReported(error) || isMarkedAsReported(causeOf(error))) {
    return true;
  }

  if (!axios.isAxiosError(error)) {
    return false;
  }

  if (error.code === 'ERR_CANCELED') {
    return true;
  }

  if (shouldSuppressToast(error)) {
    return false;
  }

  const config = error.config as CustomAxiosRequestConfig | undefined;
  if (error.response?.status === 401 && !config?._retry) {
    return false;
  }

  return error.response?.status === 402 || shouldShowGenericError(error);
};

export const reportErrorToUser = (error: unknown, message: string) => {
  logSafeAxiosError(error);

  if (!wasReportedToUser(error)) {
    showErrorToastOnce(message);
  }
};

/**
 * Statuses that are a normal outcome, not an error: Canva 401/403 means no connected account,
 * a 404 from the short-link lookup means the code is unused.
 */
export const shouldTreatAsSuccess = (config: CustomAxiosRequestConfig, status: number): boolean => {
  if (
    (status === 403 && config.url?.includes('canva/')) ||
    (status === 401 && config.url?.includes('canva/design'))
  ) {
    return true;
  }

  if (config.url?.includes('links') && status === 404) {
    return true;
  }

  return status >= 200 && status < 300;
};

export const shouldShowGenericError = (error: AxiosError): boolean => {
  const status = error.response?.status;
  const config = error.config as CustomAxiosRequestConfig | undefined;
  const url = config?.url ?? '';

  if (error.code === 'ERR_NETWORK') {
    return true;
  }

  const bannedWordsErrorCode = 422;
  const phoneFailedDependencyErrorCode = 424;
  const customFieldDependencyErrorCode = 409;
  const onboardingNotCompleteErrorCode = 406;

  if (
    [
      bannedWordsErrorCode,
      phoneFailedDependencyErrorCode,
      customFieldDependencyErrorCode,
      onboardingNotCompleteErrorCode,
    ].includes(status ?? 0)
  ) {
    return false;
  }

  if (url.includes('/groups/test-rules')) {
    return false;
  }

  if (url.includes('auth/token') && status === 400) {
    return false;
  }

  return true;
};
